// packages/shared-hooks/src/clients/useClientsList.ts
//
// `GET /clients` — single-page list keyed by the full filter object so
// changing `q` / `archived` / `managerId` / `cursor` spawns a fresh cache
// slot. The web list page uses `useClientsInfinite` for load-more; this
// single-page hook is retained for callers that page manually (e.g. an
// initial admin filter dialog that fetches one slice) and keeps parity
// with `useUsersList`.
import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import {
  GetClientsResponseSchema,
  type GetClientsQuery,
  type GetClientsResponse,
} from '@magimp/shared';
import { useApiClient } from '../api/context.js';
import { clientsKeys } from './keys.js';

export interface ClientsListFilter extends Partial<GetClientsQuery> {
  cursor?: string;
}

function toQuery(filter: ClientsListFilter): Record<string, string | number | boolean | undefined> {
  return {
    q: filter.q,
    archived: filter.archived,
    managerId: filter.managerId,
    cursor: filter.cursor,
    limit: filter.limit,
  };
}

export function useClientsList(filter: ClientsListFilter = {}) {
  const client = useApiClient();
  return useQuery<GetClientsResponse>({
    queryKey: clientsKeys.list(filter as Record<string, unknown>),
    queryFn: ({ signal }) =>
      client.get<GetClientsResponse>(
        '/clients',
        { signal, query: toQuery(filter) },
        GetClientsResponseSchema,
      ),
  });
}

// INVARIANT (#601 Resolved Decisions § Search / list / sorting): the
// cursor is opaque base64url(`{updatedAt, id}`); changing filter shape
// invalidates the cursor walk so `queryKey` excludes `cursor` while
// passing the cursor through `pageParam` only.
export function useClientsInfinite(filter: Omit<ClientsListFilter, 'cursor'> = {}) {
  const client = useApiClient();
  return useInfiniteQuery<
    GetClientsResponse,
    Error,
    InfiniteData<GetClientsResponse>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: clientsKeys.infinite(filter as Record<string, unknown>),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) => {
      const merged: ClientsListFilter = { ...filter };
      if (pageParam !== undefined) merged.cursor = pageParam;
      return client.get<GetClientsResponse>(
        '/clients',
        { signal, query: toQuery(merged) },
        GetClientsResponseSchema,
      );
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
